import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PackageCheck } from "lucide-react";
import PartyAutocomplete from "@/components/shared/PartyAutocomplete";

export function ChallanDialog({ open, onClose, challanForm, setChallanForm, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Challan Entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Challan / Invoice No. *</Label>
            <Input value={challanForm.challan_no || ""} onChange={e => setChallanForm(p => ({ ...p, challan_no: e.target.value }))} className="h-11" data-testid="challan-no-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Challan Date</Label>
            <Input type="date" value={challanForm.challan_date || ""} onChange={e => setChallanForm(p => ({ ...p, challan_date: e.target.value }))} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Description</Label>
            <Input value={challanForm.description || ""} onChange={e => setChallanForm(p => ({ ...p, description: e.target.value }))} className="h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity</Label>
            <Input value={challanForm.quantity || ""} onChange={e => setChallanForm(p => ({ ...p, quantity: e.target.value }))} className="h-11" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-[#1B3C87]" data-testid="save-challan-btn">Add Challan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReceiveJobWorkDialog({ open, onClose, receiveForm, setReceiveForm, onSubmit, entry, mode = "jobwork" }) {
  const qtyIssued = parseFloat(entry?.quantity || 0);
  const prevReceives = entry?.receives || [];
  const prevTotal = prevReceives.reduce((sum, r) => sum + (parseFloat(r.qty_received) || 0), 0);
  const remaining = Math.max(0, qtyIssued - prevTotal);
  const currentQty = parseFloat(receiveForm.qty_received) || 0;
  const newTotal = prevTotal + currentQty;
  const title = mode === "rgp" ? "Receive Material Against RGP" : "Receive Job Work";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="receive-jw-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: "Chivo" }}>{title}</DialogTitle></DialogHeader>

        {/* Progress summary */}
        <div className="bg-slate-50 border rounded-lg p-3 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Qty Sent:</span>
            <span className="font-bold">{qtyIssued} {entry?.uom || ""}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Previously Received:</span>
            <span className="font-semibold text-blue-700">{prevTotal} {entry?.uom || ""}</span>
          </div>
          <div className="flex justify-between border-t pt-1">
            <span className="text-gray-500">Remaining:</span>
            <span className={`font-bold ${remaining > 0 ? "text-amber-600" : "text-emerald-600"}`}>{remaining} {entry?.uom || ""}</span>
          </div>
          {prevReceives.length > 0 && (
            <div className="border-t pt-2 mt-1">
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-1">Previous Receives ({prevReceives.length})</p>
              {prevReceives.map((r, i) => (
                <div key={r.date_of_receipt ? `${r.date_of_receipt}-${r.qty_received}` : `recv-${i}`} className="flex justify-between text-xs text-gray-500">
                  <span>{r.date_of_receipt}{r.return_challan_no ? ` — ${r.return_challan_no}` : ""}</span>
                  <span className="font-semibold">{r.qty_received} {entry?.uom || ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Date of Receipt *</Label>
            <Input type="date" value={receiveForm.date_of_receipt || ""} onChange={e => setReceiveForm(p => ({ ...p, date_of_receipt: e.target.value }))} className="h-11" data-testid="receive-date" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Return Challan / Invoice No.</Label>
            <Input value={receiveForm.return_challan_no || ""} onChange={e => setReceiveForm(p => ({ ...p, return_challan_no: e.target.value }))} className="h-11" data-testid="return-challan" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Received</Label>
            <Input value={receiveForm.material_received || ""} onChange={e => setReceiveForm(p => ({ ...p, material_received: e.target.value }))} className="h-11" data-testid="material-received" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Qty Received (this batch) *</Label>
            <Input type="number" value={receiveForm.qty_received || ""} onChange={e => setReceiveForm(p => ({ ...p, qty_received: e.target.value }))} className="h-11" placeholder={`Max: ${remaining}`} data-testid="qty-received" />
            {currentQty > 0 && (
              <p className="text-[11px] text-gray-500">
                After this: {newTotal} / {qtyIssued} received
                {newTotal >= qtyIssued ? <span className="text-emerald-600 font-semibold ml-1">(will close entry)</span> : <span className="text-amber-600 font-semibold ml-1">(partial — entry stays open)</span>}
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-receive-btn">
            <PackageCheck size={14} className="mr-1" /> Confirm Receipt
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MaterialEntryForm({ tab, form, setField, parties, processes, onSelectOrOther }) {
  const partyKey = tab === "custjw" ? "customer_name" : "party_name";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="space-y-1.5 sm:col-span-2">
        <PartyAutocomplete
          value={form[partyKey] || form.party_name || ""}
          onChange={(v) => {
            setField(partyKey, v);
            if (tab === "custjw") setField("party_name", v);
          }}
          label={tab === "custjw" ? "Customer Name *" : "Party Name *"}
          masterType="parties"
          testId={`field-${tab}-party-autocomplete`}
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Material Description *</Label>
        <Input value={form.material_description || ""} onChange={e => setField("material_description", e.target.value)} className="h-11" data-testid="field-material" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Quantity</Label>
        <Input type="number" value={form.quantity || ""} onChange={e => setField("quantity", e.target.value)} className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">UOM</Label>
        <Input value={form.uom || ""} onChange={e => setField("uom", e.target.value)} className="h-11" placeholder="Nos / Kg / Pcs" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Vehicle No.</Label>
        <Input value={form.vehicle_no || ""} onChange={e => setField("vehicle_no", e.target.value)} className="h-11" />
      </div>
      {(tab === "rgp" || tab === "nrgp") && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Challan / Invoice No. *</Label>
          <Input value={form.erp_challan_no || ""} onChange={e => setField("erp_challan_no", e.target.value)} className="h-11" placeholder="Challan / Invoice number" data-testid="field-challan-no" />
        </div>
      )}
      {tab === "rgp" && (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Tentative Return Date</Label>
          <Input type="date" value={form.tentative_return_date || ""} onChange={e => setField("tentative_return_date", e.target.value)} className="h-11" />
        </div>
      )}
      {tab === "jobwork" && (
        <>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Process Type</Label>
            <Select value={form.process_type || ""} onValueChange={v => onSelectOrOther("process_type", v, "process_types")}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Select process" /></SelectTrigger>
              <SelectContent>
                {processes.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Direction</Label>
            <Select value={form.direction || "issued"} onValueChange={v => setField("direction", v)}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="issued">Issued (Outgoing)</SelectItem>
                <SelectItem value="received">Received (Incoming)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Document Type</Label>
            <Select value={form.doc_type || "Invoice"} onValueChange={v => setField("doc_type", v)}>
              <SelectTrigger className="h-11" data-testid="field-jw-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Invoice">Invoice</SelectItem>
                <SelectItem value="Challan">Challan (Invoice Pending)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">
              {form.doc_type === "Challan" ? "Challan No." : "Challan / Invoice No."}
            </Label>
            <Input value={form.erp_challan_no || ""} onChange={e => setField("erp_challan_no", e.target.value)} className="h-11" placeholder="Challan / Invoice number" data-testid="field-jw-challan-no" />
          </div>
        </>
      )}
      {tab === "custjw" && (
        <>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Process</Label>
            <Select value={form.process_type || ""} onValueChange={v => onSelectOrOther("process_type", v, "process_types")}>
              <SelectTrigger className="h-11" data-testid="field-cjw-process"><SelectValue placeholder="Select process" /></SelectTrigger>
              <SelectContent>
                {processes.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
                <SelectItem value="__other__" className="text-amber-600 font-semibold">Other (not in list)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Document Type</Label>
            <Select value={form.doc_type || "Invoice"} onValueChange={v => setField("doc_type", v)}>
              <SelectTrigger className="h-11" data-testid="field-cjw-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Invoice">Invoice</SelectItem>
                <SelectItem value="Challan">Challan (Invoice Pending)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">
              {form.doc_type === "Challan" ? "Challan No." : "Challan / Invoice No."}
            </Label>
            <Input value={form.challan_no || ""} onChange={e => setField("challan_no", e.target.value)} className="h-11" placeholder="Challan / Invoice number" data-testid="field-cjw-challan-no" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Expected Return Date</Label>
            <Input type="date" value={form.expected_return_date || ""} onChange={e => setField("expected_return_date", e.target.value)} className="h-11" data-testid="field-cjw-return-date" />
          </div>
        </>
      )}
      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">{tab === "custjw" ? "Contact Person" : "Person Name"}</Label>
        <Input value={form.person_name || ""} onChange={e => setField("person_name", e.target.value)} className="h-11" />
      </div>
      <div className="col-span-2 space-y-1.5">
        <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Remarks</Label>
        <Input value={form.remarks || ""} onChange={e => setField("remarks", e.target.value)} className="h-11" />
      </div>
    </div>
  );
}
